<template>
  <hs-modal v-model="open" title="Create Group Promo Code" size="md" @close="$emit('close')">
    <form id="cgpcm-form" class="cgpcm-form" @submit.prevent="handleSubmit">
      <p class="cgpcm-sub">Create a promo code that works across all stores in the group.</p>

      <hs-input
        :model-value="form.code"
        label="Promo Code"
        placeholder="e.g., SUMMER2024"
        required
        @update:modelValue="form.code = $event.toUpperCase()"
      />

      <div>
        <p class="cgpcm-field-label">Discount Type</p>
        <div class="cgpcm-type-toggle">
          <button
            type="button"
            class="cgpcm-type-btn"
            :class="{ 'is-active': form.discountType === 'percentage' }"
            @click="form.discountType = 'percentage'"
          >
            <vue-feather type="percent" size="14" />
            Percentage
          </button>
          <button
            type="button"
            class="cgpcm-type-btn"
            :class="{ 'is-active': form.discountType === 'amount' }"
            @click="form.discountType = 'amount'"
          >
            <vue-feather type="dollar-sign" size="14" />
            Fixed Amount
          </button>
        </div>
      </div>

      <hs-input
        v-model.number="form.discountValue"
        :label="`Discount Value (${form.discountType === 'percentage' ? '%' : '$'})`"
        type="number"
        :min="0"
        :max="form.discountType === 'percentage' ? 100 : undefined"
        step="0.01"
        placeholder="Enter discount value"
        required
      />

      <hs-date-input
        v-model="form.expirationDate"
        type="datetime-local"
        label="Expiration Date"
        placeholder="Select expiration date"
        required
        :min="minDateTime"
      />

      <div class="cgpcm-optional">
        <p class="cgpcm-optional__title">
          <vue-feather type="sliders" size="13" />
          Optional Settings
        </p>
        <div class="cgpcm-optional__grid">
          <hs-input
            v-model.number="form.usageLimit"
            label="Usage Limit"
            type="number"
            min="1"
            placeholder="Unlimited"
          />
          <hs-input
            v-model.number="form.minimumPurchase"
            label="Minimum Purchase"
            type="number"
            min="0"
            step="0.01"
            placeholder="No minimum"
          />
          <hs-input
            v-model.number="form.maxDiscount"
            label="Maximum Discount"
            type="number"
            min="0"
            step="0.01"
            placeholder="No maximum"
          />
        </div>
      </div>

      <hs-alert v-if="error" tone="danger" :message="error" />
    </form>

    <template #footer>
      <div class="cgpcm-footer">
        <hs-button variant="ghost" type="button" :disabled="loading" @click="$emit('close')">Cancel</hs-button>
        <hs-button
          variant="primary"
          icon="plus"
          type="submit"
          form="cgpcm-form"
          :loading="loading"
          :disabled="!isFormValid"
        >
          Create Promo Code
        </hs-button>
      </div>
    </template>
  </hs-modal>
</template>

<script>
import { ref, computed } from "vue";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import HsDateInput from "@/components/ui/HsDateInput.vue";
import { HsModal, HsButton, HsInput, HsAlert } from "@/components/ui";

export default {
  name: "CreateGroupPromoCodeModal",
  components: { HsModal, HsButton, HsInput, HsAlert, HsDateInput },
  props: {
    groupId: { type: Number, required: true },
  },
  emits: ["close", "created"],
  setup(props, { emit }) {
    const open = ref(true);
    const loading = ref(false);
    const error = ref("");

    const form = ref({
      code: "",
      discountType: "percentage",
      discountValue: null,
      expirationDate: "",
      usageLimit: null,
      minimumPurchase: null,
      maxDiscount: null,
    });

    const minDateTime = computed(() => {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      return now.toISOString().slice(0, 16);
    });

    const isFormValid = computed(() =>
      form.value.code &&
      form.value.code.length <= 12 &&
      form.value.discountValue > 0 &&
      (form.value.discountType !== "percentage" || form.value.discountValue <= 100) &&
      form.value.expirationDate
    );

    const handleSubmit = async () => {
      if (!isFormValid.value) return;
      loading.value = true;
      error.value = "";
      try {
        const expirationDate = new Date(form.value.expirationDate);
        const payload = {
          code: form.value.code.toUpperCase(),
          expiration_date: expirationDate.toISOString().slice(0, 19),
        };

        if (form.value.discountType === "percentage") payload.discount_percentage = form.value.discountValue;
        else payload.discount_amount = form.value.discountValue;

        if (form.value.usageLimit) payload.usage_limit = form.value.usageLimit;
        if (form.value.minimumPurchase) payload.minimum_purchase_required = form.value.minimumPurchase;
        if (form.value.maxDiscount) payload.max_allowed_total_discount_amount = form.value.maxDiscount;

        const response = await http.post(`/v2/groups/${props.groupId}/promo-codes`, payload);
        toast.success("Promo code created successfully!");
        emit("created", response.data);
        emit("close");
      } catch (err) {
        console.error("Failed to create promo code", err);
        error.value = err?.response?.data?.error?.message || "Unable to create promo code.";
      } finally {
        loading.value = false;
      }
    };

    return { open, loading, error, form, minDateTime, isFormValid, handleSubmit };
  },
};
</script>

<style scoped>
.cgpcm-form {
  display: grid;
  gap: 1rem;
}

.cgpcm-sub {
  color: var(--hs-muted);
  font-size: 0.83rem;
  margin: 0;
}

.cgpcm-field-label {
  color: var(--hs-text);
  font-size: 0.82rem;
  font-weight: 650;
  margin: 0 0 0.5rem;
}

.cgpcm-type-toggle {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: 1fr 1fr;
}

.cgpcm-type-btn {
  align-items: center;
  background: color-mix(in srgb, var(--hs-bg-soft) 80%, transparent);
  border: 2px solid var(--hs-border);
  border-radius: 10px;
  color: var(--hs-muted);
  cursor: pointer;
  display: flex;
  font-size: 0.85rem;
  font-weight: 600;
  gap: 0.4rem;
  justify-content: center;
  padding: 0.7rem 1rem;
  transition: all 0.15s ease;
}

.cgpcm-type-btn:hover {
  border-color: color-mix(in srgb, var(--hs-primary) 40%, transparent);
  color: var(--hs-text);
}

.cgpcm-type-btn.is-active {
  background: color-mix(in srgb, var(--hs-primary) 9%, transparent);
  border-color: var(--hs-primary);
  color: var(--hs-primary);
}

.cgpcm-optional {
  background: color-mix(in srgb, var(--hs-bg-soft) 60%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 12px;
  display: grid;
  gap: 0.85rem;
  padding: 1rem;
}

.cgpcm-optional__title {
  align-items: center;
  color: var(--hs-text);
  display: flex;
  font-size: 0.83rem;
  font-weight: 700;
  gap: 0.4rem;
  margin: 0;
}

.cgpcm-optional__grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr 1fr;
}

@media (max-width: 480px) {
  .cgpcm-optional__grid { grid-template-columns: 1fr; }
  .cgpcm-type-toggle { grid-template-columns: 1fr; }
}

.cgpcm-footer {
  display: flex;
  gap: 0.6rem;
  justify-content: flex-end;
}
</style>
